import React, { useEffect } from "react";
import { EventEmitter } from "fbemitter";
import importScript from "../../helpers/hooks/importScript";
import YouTubeType from "react-youtube";
const emitter = new EventEmitter();
import Iframe from "react-iframe";

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: any;
  }
}

export interface IYoutube extends YouTubeType {
  mute: () => void;
  destroy: () => void;
  seekTo: (value: number) => void;
  setLoop: (value: boolean) => void;
  getDuration: () => number;
  playVideo: () => void;
  playerInfo: {
    videoData: {
      [key: string]: unknown;
    };
  };
  [key: string]: unknown;
}

export interface IYoutubeState {
  data: number;
  [key: string]: unknown;
}

const YouTube = ({
  id,
  containerClassName = "",
  onReady,
  onEnd,
  mute = true,
  view = "",
  autoplay = 0,
  isIframe = true
}: {
  id: string;
  containerClassName?: string;
  view?: string;
  mute?: boolean;
  autoplay?: 0 | 1;
  onReady?: (event: Event & { target: IYoutube }) => void;
  onEnd?: (value: IYoutubeState) => void;
  isIframe?: boolean;
}) => {
  importScript(
    "https://www.youtube.com/s/player/0e7373c2/www-widgetapi.vflset/www-widgetapi.js"
  );

  useEffect(() => {
    var player: IYoutube | null = null;

    emitter.addListener("API ready", (videoId: string) => {
      const el = document.querySelector(".youtube-player-reload");
      if (el && el.parentNode) {
        el.setAttribute("id", `youtube-player-${videoId}${`-${view}` || ""}`);
      }

      // the Player object is created uniquely based on the id in props
      if (player == null) {
        player = new window.YT.Player(
          `youtube-player-${videoId}${`-${view}` || ""}`,
          {
            videoId: videoId,
            playerVars: { autoplay },
            host: "https://www.youtube-nocookie.com",
            events: {
              onReady: onPlayerReady,
              onStateChange: onPlayerEnd
            }
          }
        );
      }
    });

    return () => {
      var el = document.getElementById(
        `youtube-player-${id}${`-${view}` || ""}`
      );
      var newPlayer = document.createElement("div");

      emitter.removeAllListeners();

      if (player) {
        if (el && el.parentNode) {
          el.parentNode.insertBefore(newPlayer, el.parentNode.firstChild);
        }
        player.destroy();
      }
    };
  }, [id]);

  useEffect(() => {
    // On mount, check to see if the API script is already loaded
    if (!isIframe) {
      if (!window.YT) {
        // If not, load the script asynchronously

        window.onYouTubeIframeAPIReady = loadVideo.bind(null, id);
      } else {
        // If script is already there, load the video directly
        loadVideo(id);
      }
    }
  });

  const loadVideo = (videoId: string) => {
    emitter.emit("API ready", videoId);
  };

  const onPlayerReady = (event: Event & { target: IYoutube }) => {
    event.target.playVideo();
    if (onReady) onReady(event);
  };

  const onPlayerEnd = (state: IYoutubeState) => {
    if (state.data == 0) {
      if (onEnd) onEnd(state);
    }
  };

  return isIframe ? (
    <Iframe
      referrerpolicy="strict-origin-when-cross-origin"
      url={`https://www.youtube-nocookie.com/embed/${id}?${
        mute ? "mute=1&" : ""
      }loop=1&autoplay=1&origin=${location.href}`}
      width="100%"
      height="100%"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    />
  ) : (
    <div
      className={`${containerClassName}`}
      id={`youtube-player-${id}${`-${view}` || ""}`}
    />
  );
};

export default YouTube;
